import { sleep } from '~/shared';
import { EventHandler, IEventBusTransport } from '../types';

export class EventBusRageTransport implements IEventBusTransport {
	private readonly maxPayloadLength: number;

	private chunkedEventId = 0;

	private handlers = new Map<string, Set<EventHandler<any>>>();

	constructor(options: { maxPayloadLength?: number } = {}) {
		this.maxPayloadLength = Math.min(options.maxPayloadLength ?? 2000, 2000);
	}

	public add<T = unknown>(
		eventName: string,
		callback: EventHandler<T>,
	): () => void {
		let set = this.handlers.get(eventName);
		if (!set) {
			this.handlers.set(eventName, (set = new Set()));
		}

		set.add(callback);

		return () => set!.delete(callback);
	}

	public remove<T = unknown>(
		eventName: string,
		callback: EventHandler<T>,
	): void {
		this.handlers.get(eventName)?.delete(callback);
	}

	public sendToLocal(eventName: string, payload?: unknown): void {
		const set = this.handlers.get(eventName);
		if (!set) {
			return;
		}

		[...set].forEach((cb) => {
			try {
				cb(payload);
			} catch (e) {
				console.error('[EventBus] handler threw', e);
			}
		});
	}

	public sendToServer(eventName: string, payload?: unknown): void {
		this.sendToClient('_eventbus:server', {
			eventName,
			payload,
		});
	}

	public sendToClient(eventName: string, payload?: unknown): void {
		const payloadJson = JSON.stringify(payload) || 'null';

		if (payloadJson.length > this.maxPayloadLength) {
			this.sendChunkedEvent(eventName, payloadJson);
			return;
		}

		mp.trigger('_eventbus:client', eventName, payloadJson);
	}

	private async sendChunkedEvent(
		eventName: string,
		payloadJson: string,
	): Promise<void> {
		this.chunkedEventId++;
		const id = this.chunkedEventId;
		const chunkSize = this.maxPayloadLength;
		const payloadLength = payloadJson.length;
		const chunksCount = Math.ceil(payloadLength / chunkSize);

		for (let i = 0; i < chunksCount; i++) {
			const start = i * chunkSize;
			const end = Math.min(start + chunkSize, payloadLength);
			const chunk = payloadJson.slice(start, end);

			mp.trigger('_eventbus:chunk', JSON.stringify({ chunk, id }));
			await sleep(0);
		}

		mp.trigger(
			'_eventbus:chunk_end',
			JSON.stringify({ eventName, id, length: payloadLength }),
		);
	}
}
